import React, { useState, useEffect, useCallback, useRef } from 'react';
import { mediaAPI, BACKEND_URL } from '../api';
import {
  Upload, Search, Trash2, Star, TrendingUp, Sparkles, EyeOff, Eye,
  Filter, CheckCircle, X, Pencil, Image as ImageIcon, Loader2, Plus,
  MoveUp, MoveDown, AlertCircle
} from 'lucide-react';

const WOMEN_CATS = [
  { id: 'blouse', name: 'Blouse' },
  { id: 'suit-salwar', name: 'Suit Salwar' },
  { id: 'lehenga', name: 'Lehenga' },
  { id: 'kurta', name: 'Kurti' },
  { id: 'coat-set', name: 'Coat Set' },
];
const MEN_CATS = [
  { id: 'shirt', name: 'Shirt' },
  { id: 'blazer', name: 'Blazer' },
  { id: 'sherwani', name: 'Sherwani' },
  { id: 'kurta-pajama', name: 'Kurta' },
  { id: 'trousers', name: 'Trousers' },
];
const SUBCATS = {
  blouse: ['general', 'front', 'back', 'sleeves', 'bridal', 'simple', 'trendy'],
  'suit-salwar': ['general', 'anarkali', 'straight', 'palazzo', 'party', 'daily'],
  lehenga: ['general', 'bridal', 'reception', 'sangeet', 'pastel'],
  kurta: ['general', 'casual', 'office', 'party', 'printed'],
  'coat-set': ['general', 'formal', 'cape', 'belted'],
  shirt: ['general', 'formal', 'casual', 'print'],
  blazer: ['general', 'tuxedo', 'wedding', 'slim'],
  sherwani: ['general', 'groom', 'reception', 'jodhpuri'],
  'kurta-pajama': ['general', 'festive', 'wedding', 'daily'],
  trousers: ['general', 'formal', 'chino', 'slim'],
};

const GOLD = '#D4AF37';
const INK = '#0F0F0F';
const MUTED = '#595959';
const LINE = '#E5E5E5';
const SOFT = '#FAF9F6';

const MediaManager = () => {
  const [gender, setGender] = useState('women');
  const [category, setCategory] = useState('blouse');
  const [subcategory, setSubcategory] = useState('general');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]); // [{file, progress, status}]
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState(''); // '', 'featured', 'trending', 'new', 'hidden'
  const [dragOver, setDragOver] = useState(false);
  const [editing, setEditing] = useState(null); // media id being edited
  const [toast, setToast] = useState(null);
  const [cloudStatus, setCloudStatus] = useState(null); // {configured, cloud_name, message}
  const fileInputRef = useRef(null);

  const cats = gender === 'women' ? WOMEN_CATS : MEN_CATS;

  // Check Cloudinary config on mount
  useEffect(() => {
    mediaAPI.configStatus()
      .then(setCloudStatus)
      .catch(() => setCloudStatus({ configured: false, message: 'Could not verify Cloudinary status' }));
  }, []);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { category };
      if (filter === 'featured') params.featured = true;
      else if (filter === 'trending') params.trending = true;
      else if (filter === 'new') params.is_new = true;
      else if (filter === 'hidden') params.hidden = true;
      if (search) params.search = search;
      const data = await mediaAPI.list(params);
      setItems(data);
    } catch (err) {
      showToast(err.message || 'Load failed', 'error');
    } finally {
      setLoading(false);
    }
  }, [category, filter, search]);

  useEffect(() => { load(); }, [load]);

  // ─── Switch category resets subcategory ───
  useEffect(() => {
    if (!SUBCATS[category]?.includes(subcategory)) {
      setSubcategory(SUBCATS[category]?.[0] || 'general');
    }
  }, [category, subcategory]);

  // ─── Upload (prices added via quick prompt defaults OR inline) ───
  const [defaultPrice, setDefaultPrice] = useState({ original_price: '', selling_price: '', offer_tag: '' });

  const handleFiles = async (files) => {
    const fileList = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (!fileList.length) return;

    const queue = fileList.map((f, i) => ({ id: `${Date.now()}-${i}`, file: f, progress: 0, status: 'pending' }));
    setUploadQueue(q => [...q, ...queue]);

    for (const entry of queue) {
      setUploadQueue(q => q.map(x => x.id === entry.id ? { ...x, status: 'uploading' } : x));
      try {
        const fd = new FormData();
        fd.append('file', entry.file);
        fd.append('category', category);
        fd.append('subcategory', subcategory);
        fd.append('title', entry.file.name.replace(/\.[^.]+$/, ''));
        // Apply default pricing to bulk uploads (admin edits per-item after)
        if (defaultPrice.original_price) fd.append('original_price', defaultPrice.original_price);
        if (defaultPrice.selling_price) fd.append('selling_price', defaultPrice.selling_price);
        if (defaultPrice.offer_tag) fd.append('offer_tag', defaultPrice.offer_tag);
        await mediaAPI.upload(fd);
        setUploadQueue(q => q.map(x => x.id === entry.id ? { ...x, status: 'done', progress: 100 } : x));
      } catch (err) {
        setUploadQueue(q => q.map(x => x.id === entry.id ? { ...x, status: 'error', error: err.message } : x));
      }
    }
    showToast(`${fileList.length} file${fileList.length > 1 ? 's' : ''} uploaded`);
    await load();
    setTimeout(() => setUploadQueue(q => q.filter(x => x.status !== 'done')), 1500);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  // ─── Toggle flags ───
  const toggleFlag = async (m, key) => {
    try {
      await mediaAPI.update(m.id, { [key]: !m[key] });
      await load();
    } catch (err) {
      showToast(err.message || 'Update failed', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this image? This cannot be undone.')) return;
    try {
      await mediaAPI.remove(id);
      showToast('Deleted');
      await load();
    } catch (err) {
      showToast(err.message || 'Delete failed', 'error');
    }
  };

  const handleReorder = async (id, direction) => {
    const idx = items.findIndex(x => x.id === id);
    if (idx < 0) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= items.length) return;
    const a = items[idx], b = items[targetIdx];
    try {
      await mediaAPI.reorder([
        { id: a.id, display_order: b.display_order },
        { id: b.id, display_order: a.display_order },
      ]);
      await load();
    } catch (err) {
      showToast(err.message || 'Reorder failed', 'error');
    }
  };

  const filtered = items.filter(m => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (m.title || '').toLowerCase().includes(s) ||
      (m.tags || []).some(t => t.toLowerCase().includes(s)) ||
      (m.subcategory || '').toLowerCase().includes(s);
  });

  return (
    <div data-testid="media-manager" className="pb-6">
      {/* Toast */}
      {toast && (
        <div className="fixed top-16 right-4 z-50 px-3 py-2 text-[11px] shadow-lg"
          style={{ backgroundColor: toast.type === 'error' ? '#991B1B' : '#166534', color: '#FFF' }}>
          {toast.msg}
        </div>
      )}

      {/* Cloudinary config warning */}
      {cloudStatus && !cloudStatus.configured && (
        <div data-testid="cloudinary-warning" className="mb-3 p-3 border flex items-start gap-2" style={{ borderColor: '#991B1B', backgroundColor: 'rgba(153,27,27,0.05)' }}>
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#991B1B' }} strokeWidth={1.5} />
          <div>
            <p className="text-[11px] font-medium" style={{ color: '#991B1B' }}>Cloudinary not configured</p>
            <p className="text-[10px] mt-0.5" style={{ color: MUTED }}>
              Add <code style={{ fontFamily: 'monospace', backgroundColor: '#FFF', padding: '0 4px' }}>CLOUDINARY_CLOUD_NAME</code>,{' '}
              <code style={{ fontFamily: 'monospace', backgroundColor: '#FFF', padding: '0 4px' }}>CLOUDINARY_API_KEY</code>,{' '}
              <code style={{ fontFamily: 'monospace', backgroundColor: '#FFF', padding: '0 4px' }}>CLOUDINARY_API_SECRET</code>{' '}
              to deployment secrets and redeploy. Uploads will fail until configured.
            </p>
          </div>
        </div>
      )}
      {cloudStatus && cloudStatus.configured && (
        <div data-testid="cloudinary-ok" className="mb-3 px-3 py-1.5 border flex items-center gap-2" style={{ borderColor: '#166534', backgroundColor: 'rgba(22,101,52,0.05)' }}>
          <CheckCircle className="w-3.5 h-3.5" style={{ color: '#166534' }} strokeWidth={1.5} />
          <p className="text-[10px]" style={{ color: '#166534' }}>
            Cloudinary ready — cloud: <b>{cloudStatus.cloud_name}</b>. All uploads stored on CDN.
          </p>
        </div>
      )}

      {/* Gender tabs */}
      <div className="flex gap-2 mb-3">
        {['women', 'men'].map(g => (
          <button key={g} data-testid={`media-gender-${g}`} onClick={() => {
            setGender(g);
            setCategory(g === 'women' ? 'blouse' : 'shirt');
          }}
            className="flex-1 py-2 text-[11px] tracking-[0.08em] uppercase"
            style={{ backgroundColor: gender === g ? INK : '#FFF', color: gender === g ? '#FFF' : MUTED, border: `1px solid ${gender === g ? INK : LINE}` }}>
            {g}
          </button>
        ))}
      </div>

      {/* Category select */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-hide mb-3 pb-1">
        {cats.map(c => (
          <button key={c.id} data-testid={`media-cat-${c.id}`} onClick={() => setCategory(c.id)}
            className="flex-shrink-0 px-3 py-1.5 text-[10px] tracking-[0.06em] uppercase whitespace-nowrap"
            style={{ backgroundColor: category === c.id ? GOLD : '#FFF', color: category === c.id ? '#FFF' : MUTED, border: `1px solid ${category === c.id ? GOLD : LINE}` }}>
            {c.name}
          </button>
        ))}
      </div>

      {/* Subcategory select */}
      <div className="flex gap-1 overflow-x-auto scrollbar-hide mb-3">
        {(SUBCATS[category] || ['general']).map(s => (
          <button key={s} data-testid={`media-sub-${s}`} onClick={() => setSubcategory(s)}
            className="flex-shrink-0 px-2.5 py-1 text-[9px] uppercase tracking-[0.05em] whitespace-nowrap"
            style={{ backgroundColor: subcategory === s ? 'rgba(212,175,55,0.12)' : 'transparent', color: subcategory === s ? GOLD : '#999', border: `1px solid ${subcategory === s ? GOLD : LINE}` }}>
            {s}
          </button>
        ))}
      </div>

      {/* Default pricing for bulk uploads */}
      <div className="mb-3 bg-white border p-3" style={{ borderColor: LINE }}>
        <p className="text-[9px] tracking-[0.12em] uppercase mb-2" style={{ color: MUTED }}>Default Pricing (applied to new uploads)</p>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-[9px]" style={{ color: MUTED }}>Original ₹</label>
            <input data-testid="default-original-price" type="number" min="0" value={defaultPrice.original_price}
              onChange={e => setDefaultPrice(p => ({ ...p, original_price: e.target.value }))}
              placeholder="2499"
              className="w-full py-1 border-b text-[12px] focus:outline-none focus:border-[#0F0F0F]"
              style={{ borderColor: LINE, color: INK }} />
          </div>
          <div>
            <label className="text-[9px]" style={{ color: MUTED }}>Selling ₹</label>
            <input data-testid="default-selling-price" type="number" min="0" value={defaultPrice.selling_price}
              onChange={e => setDefaultPrice(p => ({ ...p, selling_price: e.target.value }))}
              placeholder="1499"
              className="w-full py-1 border-b text-[12px] focus:outline-none focus:border-[#0F0F0F]"
              style={{ borderColor: LINE, color: INK }} />
          </div>
          <div>
            <label className="text-[9px]" style={{ color: MUTED }}>Offer tag</label>
            <input data-testid="default-offer-tag" type="text" value={defaultPrice.offer_tag}
              onChange={e => setDefaultPrice(p => ({ ...p, offer_tag: e.target.value }))}
              placeholder="Festive -40%"
              className="w-full py-1 border-b text-[12px] focus:outline-none focus:border-[#0F0F0F]"
              style={{ borderColor: LINE, color: INK }} />
          </div>
        </div>
        <p className="text-[9px] mt-1.5" style={{ color: '#BFBFBF' }}>Leave blank to upload without prices — you can set per-item from the edit modal</p>
      </div>

      {/* Upload zone */}
      <div
        data-testid="media-upload-zone"
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className="border-2 border-dashed p-5 mb-3 cursor-pointer transition-all"
        style={{
          borderColor: dragOver ? GOLD : LINE,
          backgroundColor: dragOver ? 'rgba(212,175,55,0.06)' : '#FFF',
        }}>
        <div className="flex flex-col items-center gap-1.5">
          <Upload className="w-6 h-6" style={{ color: dragOver ? GOLD : MUTED }} strokeWidth={1.5} />
          <p className="text-[12px]" style={{ color: INK, fontWeight: 500 }}>Drop images or click to upload</p>
          <p className="text-[10px]" style={{ color: MUTED }}>
            Target: <b>{cats.find(c => c.id === category)?.name}</b> / <b>{subcategory}</b>
          </p>
          <p className="text-[9px]" style={{ color: '#BFBFBF' }}>JPEG / PNG / WEBP • up to 10MB • auto-compressed</p>
        </div>
        <input ref={fileInputRef} data-testid="media-file-input" type="file" multiple accept="image/*" className="hidden"
          onChange={e => handleFiles(e.target.files)} />
      </div>

      {/* Upload queue */}
      {uploadQueue.length > 0 && (
        <div className="mb-3 space-y-1">
          {uploadQueue.map(q => (
            <div key={q.id} className="flex items-center gap-2 px-3 py-2 bg-white border text-[10px]" style={{ borderColor: LINE }}>
              {q.status === 'uploading' && <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: GOLD }} />}
              {q.status === 'done' && <CheckCircle className="w-3.5 h-3.5" style={{ color: '#166534' }} />}
              {q.status === 'error' && <AlertCircle className="w-3.5 h-3.5" style={{ color: '#991B1B' }} />}
              <span className="flex-1 truncate" style={{ color: INK }}>{q.file.name}</span>
              <span style={{ color: q.status === 'error' ? '#991B1B' : MUTED }}>
                {q.status === 'uploading' ? 'Uploading...' : q.status === 'done' ? 'Done' : q.status === 'error' ? (q.error || 'Failed') : 'Pending'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Search + filters */}
      <div className="flex gap-1.5 mb-3">
        <div className="flex-1 relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: MUTED }} strokeWidth={1.5} />
          <input data-testid="media-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search title, tag, subcategory..."
            className="w-full pl-8 pr-2 py-1.5 text-[11px] bg-white border focus:outline-none"
            style={{ borderColor: LINE, color: INK }} />
        </div>
        {[
          { id: '', label: 'All' },
          { id: 'featured', label: 'Featured', icon: Star },
          { id: 'trending', label: 'Trending', icon: TrendingUp },
          { id: 'new', label: 'New', icon: Sparkles },
          { id: 'hidden', label: 'Hidden', icon: EyeOff },
        ].map(f => (
          <button key={f.id} data-testid={`media-filter-${f.id || 'all'}`} onClick={() => setFilter(f.id)}
            className="px-2 py-1.5 text-[9px] tracking-[0.05em] uppercase flex items-center gap-1"
            style={{ backgroundColor: filter === f.id ? INK : '#FFF', color: filter === f.id ? '#FFF' : MUTED, border: `1px solid ${filter === f.id ? INK : LINE}` }}>
            {f.icon && <f.icon className="w-2.5 h-2.5" strokeWidth={1.5} />}
            {f.label}
          </button>
        ))}
      </div>

      {/* Count */}
      <div className="flex items-center justify-between mb-2 px-1">
        <p className="text-[10px]" style={{ color: MUTED }}>
          {loading ? 'Loading...' : `${filtered.length} image${filtered.length !== 1 ? 's' : ''} in ${category}`}
        </p>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 gap-2" data-testid="media-grid">
        {filtered.map((m, idx) => {
          // Cloudinary URLs are full HTTPS URLs; legacy local /uploads/... fallback kept for back-compat
          const src = m.url && m.url.startsWith('http') ? m.url : `${BACKEND_URL}${m.url}`;
          // For Cloudinary URLs, inject responsive crop transformation for the grid thumbnail
          const thumb = src.includes('/upload/') && src.includes('res.cloudinary.com')
            ? src.replace('/upload/', '/upload/c_fill,w_400,h_530,g_auto,q_auto,f_auto/')
            : src;
          return (
            <div key={m.id} data-testid={`media-item-${m.id}`}
              className="bg-white border relative group" style={{ borderColor: m.hidden ? '#991B1B' : LINE }}>
              <div className="aspect-[3/4] overflow-hidden bg-[#FAF9F6]">
                <img src={thumb} alt={m.title} loading="lazy"
                  className="w-full h-full object-cover"
                  onError={e => { e.target.style.opacity = '0.3'; }} />
                {/* Flags overlay */}
                <div className="absolute top-1 left-1 flex flex-col gap-1">
                  {m.featured && <span className="px-1.5 py-0.5 text-[8px] tracking-[0.05em] uppercase text-white" style={{ backgroundColor: GOLD }}>Featured</span>}
                  {m.trending && <span className="px-1.5 py-0.5 text-[8px] tracking-[0.05em] uppercase text-white" style={{ backgroundColor: '#991B1B' }}>Trending</span>}
                  {m.is_new && <span className="px-1.5 py-0.5 text-[8px] tracking-[0.05em] uppercase text-white" style={{ backgroundColor: '#166534' }}>New</span>}
                  {m.hidden && <span className="px-1.5 py-0.5 text-[8px] tracking-[0.05em] uppercase text-white bg-black/70">Hidden</span>}
                </div>
                {/* Hover actions */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1" style={{ backgroundColor: 'rgba(15,15,15,0.35)' }}>
                  <button data-testid={`media-edit-${m.id}`} onClick={() => setEditing(m)} className="w-8 h-8 bg-white flex items-center justify-center" title="Edit">
                    <Pencil className="w-3.5 h-3.5" style={{ color: INK }} strokeWidth={1.5} />
                  </button>
                  <button data-testid={`media-reorder-up-${m.id}`} onClick={() => handleReorder(m.id, 'up')} disabled={idx === 0} className="w-8 h-8 bg-white flex items-center justify-center disabled:opacity-30" title="Move up">
                    <MoveUp className="w-3.5 h-3.5" style={{ color: INK }} strokeWidth={1.5} />
                  </button>
                  <button data-testid={`media-reorder-down-${m.id}`} onClick={() => handleReorder(m.id, 'down')} disabled={idx === filtered.length - 1} className="w-8 h-8 bg-white flex items-center justify-center disabled:opacity-30" title="Move down">
                    <MoveDown className="w-3.5 h-3.5" style={{ color: INK }} strokeWidth={1.5} />
                  </button>
                  <button data-testid={`media-delete-${m.id}`} onClick={() => handleDelete(m.id)} className="w-8 h-8 bg-white flex items-center justify-center" title="Delete">
                    <Trash2 className="w-3.5 h-3.5" style={{ color: '#991B1B' }} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
              <div className="p-2">
                <p className="text-[11px] truncate" style={{ color: INK, fontWeight: 500 }}>{m.title}</p>
                <p className="text-[9px] truncate" style={{ color: MUTED }}>{m.category} • {m.subcategory} • {Math.round((m.size_bytes || 0) / 1024)}KB</p>
                {/* Admin prices */}
                <div className="mt-1 flex items-baseline gap-1">
                  {m.selling_price > 0 ? (
                    <>
                      <span className="text-[12px] font-medium" style={{ color: INK }}>₹{Number(m.selling_price).toLocaleString('en-IN')}</span>
                      {m.original_price > m.selling_price && (
                        <>
                          <span className="text-[9px] line-through" style={{ color: '#999' }}>₹{Number(m.original_price).toLocaleString('en-IN')}</span>
                          <span className="text-[8px]" style={{ color: '#166534' }}>
                            {Math.round((1 - m.selling_price / m.original_price) * 100)}% off
                          </span>
                        </>
                      )}
                    </>
                  ) : (
                    <span className="text-[10px]" style={{ color: '#991B1B' }}>⚠ No price set</span>
                  )}
                </div>
                {m.offer_tag && (
                  <span className="inline-block mt-1 text-[8px] tracking-[0.05em] uppercase text-white px-1.5 py-0.5" style={{ backgroundColor: GOLD }}>
                    {m.offer_tag}
                  </span>
                )}
                <div className="flex gap-1 mt-1.5">
                  <button data-testid={`media-toggle-featured-${m.id}`} onClick={() => toggleFlag(m, 'featured')} className="p-1" title="Featured">
                    <Star className="w-3 h-3" style={{ color: m.featured ? GOLD : '#BFBFBF', fill: m.featured ? GOLD : 'transparent' }} strokeWidth={1.5} />
                  </button>
                  <button data-testid={`media-toggle-trending-${m.id}`} onClick={() => toggleFlag(m, 'trending')} className="p-1" title="Trending">
                    <TrendingUp className="w-3 h-3" style={{ color: m.trending ? '#991B1B' : '#BFBFBF' }} strokeWidth={1.5} />
                  </button>
                  <button data-testid={`media-toggle-new-${m.id}`} onClick={() => toggleFlag(m, 'is_new')} className="p-1" title="New">
                    <Sparkles className="w-3 h-3" style={{ color: m.is_new ? '#166534' : '#BFBFBF' }} strokeWidth={1.5} />
                  </button>
                  <button data-testid={`media-toggle-hidden-${m.id}`} onClick={() => toggleFlag(m, 'hidden')} className="p-1" title={m.hidden ? 'Show' : 'Hide'}>
                    {m.hidden ? <EyeOff className="w-3 h-3" style={{ color: '#991B1B' }} strokeWidth={1.5} /> : <Eye className="w-3 h-3" style={{ color: '#BFBFBF' }} strokeWidth={1.5} />}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div className="py-10 text-center bg-white border" style={{ borderColor: LINE }}>
          <ImageIcon className="w-8 h-8 mx-auto mb-2" style={{ color: '#BFBFBF' }} strokeWidth={1.5} />
          <p className="text-[12px]" style={{ color: MUTED }}>No images yet for {category} / {subcategory}</p>
          <p className="text-[10px] mt-1" style={{ color: '#BFBFBF' }}>Drop files above to start</p>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <EditModal
          media={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); showToast('Saved'); }}
        />
      )}

      <style>{`.scrollbar-hide::-webkit-scrollbar{display:none} .scrollbar-hide{-ms-overflow-style:none;scrollbar-width:none}`}</style>
    </div>
  );
};

// ─── Edit Modal ───
const ALL_CATS = [
  { id: 'blouse', name: 'Blouse (W)' },
  { id: 'suit-salwar', name: 'Suit Salwar (W)' },
  { id: 'lehenga', name: 'Lehenga (W)' },
  { id: 'kurta', name: 'Kurti (W)' },
  { id: 'coat-set', name: 'Coat Set (W)' },
  { id: 'shirt', name: 'Shirt (M)' },
  { id: 'blazer', name: 'Blazer (M)' },
  { id: 'sherwani', name: 'Sherwani (M)' },
  { id: 'kurta-pajama', name: 'Kurta (M)' },
  { id: 'coat-pant', name: 'Coat Pant (M)' },
  { id: 'trousers', name: 'Trousers (M)' },
];

const EditModal = ({ media, onClose, onSaved }) => {
  const [title, setTitle] = useState(media.title || '');
  const [categoryVal, setCategoryVal] = useState(media.category || 'blouse');
  const [subcategory, setSubcategory] = useState(media.subcategory || 'general');
  const [tags, setTags] = useState((media.tags || []).join(', '));
  const [originalPrice, setOriginalPrice] = useState(media.original_price || '');
  const [sellingPrice, setSellingPrice] = useState(media.selling_price || '');
  const [offerTag, setOfferTag] = useState(media.offer_tag || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const src = media.url && media.url.startsWith('http') ? media.url : `${BACKEND_URL}${media.url}`;
  const subs = SUBCATS[categoryVal] || ['general'];

  useEffect(() => {
    // reset subcategory if current one doesn't belong to new category
    if (!subs.includes(subcategory)) setSubcategory(subs[0] || 'general');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryVal]);

  const discountPct = originalPrice > 0 && sellingPrice > 0 && sellingPrice < originalPrice
    ? Math.round((1 - sellingPrice / originalPrice) * 100)
    : 0;

  const handleSave = async () => {
    if (sellingPrice && originalPrice && Number(sellingPrice) > Number(originalPrice)) {
      setError('Selling price cannot exceed original price');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await mediaAPI.update(media.id, {
        title,
        category: categoryVal,
        subcategory,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        original_price: Number(originalPrice) || 0,
        selling_price: Number(sellingPrice) || 0,
        discount_percent: discountPct,
        offer_tag: offerTag,
      });
      onSaved();
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center overflow-y-auto" style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
      <div className="bg-white w-full max-w-md p-5 max-h-[92vh] overflow-y-auto" data-testid="media-edit-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[14px]" style={{ fontFamily: 'Playfair Display, serif', color: INK }}>Edit Product</h3>
          <button data-testid="media-edit-close" onClick={onClose}><X className="w-4 h-4" style={{ color: MUTED }} strokeWidth={1.5} /></button>
        </div>
        <div className="aspect-[3/4] max-h-40 overflow-hidden mx-auto mb-3 bg-[#FAF9F6]">
          <img src={src} alt={title} className="w-full h-full object-cover" />
        </div>
        <div className="space-y-2.5">
          <div>
            <label className="text-[9px] tracking-[0.12em] uppercase" style={{ color: MUTED }}>Product Name</label>
            <input data-testid="media-edit-title" value={title} onChange={e => setTitle(e.target.value)}
              className="w-full py-1.5 border-b text-[12px] focus:outline-none focus:border-[#0F0F0F]" style={{ borderColor: LINE, color: INK }} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[9px] tracking-[0.12em] uppercase" style={{ color: MUTED }}>Category</label>
              <select data-testid="media-edit-category" value={categoryVal} onChange={e => setCategoryVal(e.target.value)}
                className="w-full py-1.5 border-b text-[12px] bg-transparent focus:outline-none" style={{ borderColor: LINE, color: INK }}>
                {ALL_CATS.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[9px] tracking-[0.12em] uppercase" style={{ color: MUTED }}>Subcategory</label>
              <select data-testid="media-edit-subcat" value={subcategory} onChange={e => setSubcategory(e.target.value)}
                className="w-full py-1.5 border-b text-[12px] bg-transparent focus:outline-none" style={{ borderColor: LINE, color: INK }}>
                {subs.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          {/* ─── PRICING SECTION ─── */}
          <div className="mt-2 p-2.5" style={{ backgroundColor: 'rgba(212,175,55,0.04)', border: `1px solid ${GOLD}` }}>
            <p className="text-[9px] tracking-[0.12em] uppercase mb-1.5" style={{ color: GOLD }}>Pricing (Admin-controlled)</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[9px]" style={{ color: MUTED }}>Original ₹</label>
                <input data-testid="media-edit-original-price" type="number" min="0" value={originalPrice}
                  onChange={e => setOriginalPrice(e.target.value)} placeholder="2499"
                  className="w-full py-1 border-b text-[12px] focus:outline-none focus:border-[#0F0F0F]" style={{ borderColor: LINE, color: INK }} />
              </div>
              <div>
                <label className="text-[9px]" style={{ color: MUTED }}>Selling ₹</label>
                <input data-testid="media-edit-selling-price" type="number" min="0" value={sellingPrice}
                  onChange={e => setSellingPrice(e.target.value)} placeholder="1499"
                  className="w-full py-1 border-b text-[12px] focus:outline-none focus:border-[#0F0F0F]" style={{ borderColor: LINE, color: INK }} />
              </div>
            </div>
            <div className="flex items-center justify-between mt-1.5">
              <input data-testid="media-edit-offer-tag" type="text" value={offerTag}
                onChange={e => setOfferTag(e.target.value)} placeholder="Offer tag (e.g., Festive Special)"
                className="flex-1 py-1 border-b text-[11px] focus:outline-none focus:border-[#0F0F0F]" style={{ borderColor: LINE, color: INK }} />
              {discountPct > 0 && (
                <span data-testid="discount-preview" className="ml-2 text-[10px] px-2 py-0.5" style={{ backgroundColor: '#DCFCE7', color: '#166534' }}>
                  {discountPct}% OFF
                </span>
              )}
            </div>
          </div>
          <div>
            <label className="text-[9px] tracking-[0.12em] uppercase" style={{ color: MUTED }}>Tags (comma-separated)</label>
            <input data-testid="media-edit-tags" value={tags} onChange={e => setTags(e.target.value)}
              placeholder="premium, silk, wedding"
              className="w-full py-1.5 border-b text-[12px] focus:outline-none focus:border-[#0F0F0F]" style={{ borderColor: LINE, color: INK }} />
          </div>
          {error && <p data-testid="media-edit-error" className="text-[10px]" style={{ color: '#991B1B' }}>{error}</p>}
          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 text-[10px] tracking-[0.08em] uppercase border" style={{ borderColor: LINE, color: MUTED }}>Cancel</button>
            <button data-testid="media-edit-save" onClick={handleSave} disabled={saving}
              className="flex-1 py-2.5 text-[10px] tracking-[0.08em] uppercase text-white disabled:opacity-50"
              style={{ backgroundColor: INK }}>
              {saving ? 'Saving...' : 'Save Product'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaManager;
